import { workerData } from "node:worker_threads";

import type { BuildTask, GeneratedForm } from "../../../../../Generator/src";
import { iterateEntries } from "../../../../../Common/IterableUtils";

import { importGenerator } from "./GeneratorRequireUtils";
import { defineWorkerHandler } from "./TypedThreadWorker";

export interface GeneratedFormWorkerMessage {
    [relativeFileName: string]: string | ArrayBuffer;
}

export type CallResponse =
    | { type: "Result"; gfv: string; generatedForm: GeneratedFormWorkerMessage }
    | { type: "Error"; gfv: string; errorText: string };

defineWorkerHandler<BuildTask, CallResponse>(async (task, send): Promise<void> => {
    try {
        const { result, transferList } = await generate(task);
        const msg = { type: "Result", gfv: task.gfv, generatedForm: result } satisfies CallResponse;
        send(msg, transferList);
    } catch (error) {
        const errorText = error instanceof Error ? `${error.toString()}\n${error.stack}` : `${error}`;
        const msg = { type: "Error", gfv: task.gfv, errorText: errorText } satisfies CallResponse;
        send(msg);
    }
});

const { generatorPath } = workerData as { generatorPath: string };
const generator = importGenerator(generatorPath);

const generate = async (task: BuildTask) => {
    const formContent = await generator.generate(task);
    return writeFormToBuffer(formContent);
};

/**
 * Приводим сырые данные к ArrayBuffer,
 * т.к. Buffer не может быть структурно копирован
 * или передан между потоками
 */
const writeFormToBuffer = (formContent: GeneratedForm) => {
    const result: GeneratedFormWorkerMessage = {};
    const transferList: ArrayBuffer[] = [];
    for (const [key, formContentItem] of iterateEntries(formContent)) {
        if (formContentItem instanceof Buffer) {
            // Buffer.buffer is typed as ArrayBuffer | SharedArrayBuffer,
            // but Node.js Buffers never use SharedArrayBuffer in practice.
            const content = formContentItem.buffer.slice(
                formContentItem.byteOffset,
                formContentItem.byteOffset + formContentItem.byteLength
            ) as ArrayBuffer;
            result[key] = content;
            transferList.push(content);
        } else {
            result[key] = formContentItem as string;
        }
    }
    return { result: result, transferList: transferList };
};
